// FE-SCRM-052 — Opportunities list table.
//
// Sortable column headers; clicking a header cycles
// ``asc → desc → default``. The leftmost name column links to the
// opportunity detail page; the prospect cell links to the future
// Prospect detail page (FE-SCRM-050; pre-route link stays valid
// once the route lands).

import { Link, useSearchParams } from "react-router-dom";

import { Table, TBody, THead, Th, Td, Tr } from "@/design-system";
import { MoneyCell } from "@/features/sales/components";

import {
  CADENCE_HEALTH_LABEL,
  LIVES_BAND_LABEL,
  STAGE_LABEL,
  TYPE_LABEL,
  formatPercent,
  formatRelativeDate,
  formatRenewal,
} from "./labels";
import type { OpportunityListRow } from "./types";
import {
  DEFAULT_ORDERING,
  readOrderingFromUrl,
  writeOrderingToParams,
} from "./urlState";


export interface SortHeaderProps {
  field: string;
  label: string;
}


function nextOrdering(field: string, current: string): string {
  const desc = `-${field}`;
  if (current === field) return desc;
  if (current === desc) return DEFAULT_ORDERING;
  return field;
}


function SortHeader({ field, label }: SortHeaderProps) {
  const [params, setParams] = useSearchParams();
  const current = readOrderingFromUrl(params);
  const isActive = current === field || current === `-${field}`;
  const indicator = current === field ? "▲" : current === `-${field}` ? "▼" : "";

  return (
    <Th className="text-xs font-semibold">
      <button
        type="button"
        data-testid={`sort-header-${field}`}
        onClick={() => {
          const next = nextOrdering(field, current);
          const out = writeOrderingToParams(next, params);
          // Sort change resets pagination so the user doesn't land
          // on a now-empty page-N.
          out.delete("page");
          setParams(out, { replace: false });
        }}
        className={[
          "inline-flex items-center gap-1 text-left",
          isActive ? "text-text-primary" : "text-text-tertiary",
          "hover:text-text-primary",
        ].join(" ")}
      >
        {label}
        {indicator && <span aria-hidden="true">{indicator}</span>}
      </button>
    </Th>
  );
}


// Cadence-health pills on brand tokens (was off-token red/amber/emerald/sky):
// critical→ember, behind→gold, on_track→sage, ahead→teal, else neutral.
const CADENCE_TONE: Record<string, string> = {
  critical: "bg-ember-tint text-ember-ink",
  behind: "bg-gold-tint text-gold-ink",
  on_track: "bg-sage-tint text-sage-ink",
  ahead: "bg-teal-tint text-teal",
  not_yet_urgent: "bg-recessed text-text-secondary",
};


export interface OpportunityTableProps {
  rows: OpportunityListRow[];
}


export function OpportunityTable({ rows }: OpportunityTableProps) {
  return (
    <Table
      layout="fixed"
      wrapperClassName="rounded-lg border border-border-subtle"
      data-testid="opportunities-table-wrapper"
    >
      <THead className="bg-recessed">
        <Tr>
          <SortHeader field="name" label="Name" />
          <SortHeader field="prospect_name" label="Prospect" />
          <SortHeader field="opportunity_type" label="Type" />
          <SortHeader field="stage" label="Stage" />
          <SortHeader field="lives_band" label="Lives band" />
          <SortHeader
            field="estimated_annual_revenue_cents" label="Value"
          />
          <SortHeader field="probability" label="Probability" />
          <SortHeader field="renewal_year" label="Renewal" />
          <SortHeader field="cadence_health" label="Cadence" />
          <SortHeader field="last_touchpoint_at" label="Last touchpoint" />
          <SortHeader field="created_at" label="Created" />
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => (
          <Tr
            key={row.opportunity_id}
            data-testid={`opportunity-row-${row.opportunity_id}`}
            className="border-t border-border-subtle hover:bg-canvas/50"
          >
            <Td>
              <Link
                to={`/dashboard/sales-crm/opportunities/${row.opportunity_id}`}
                className="font-medium text-teal hover:underline"
              >
                {row.name}
              </Link>
            </Td>
            <Td className="text-text-secondary">
              <Link
                to={`/dashboard/sales-crm/prospects/${row.prospect_id}`}
                className="hover:underline"
              >
                {row.prospect_name ?? "—"}
              </Link>
            </Td>
            <Td className="text-text-secondary">
              {TYPE_LABEL[row.opportunity_type] ?? row.opportunity_type}
            </Td>
            <Td className="text-text-secondary">
              {STAGE_LABEL[row.stage] ?? row.stage}
            </Td>
            <Td className="text-text-secondary">
              {row.lives_band
                ? LIVES_BAND_LABEL[row.lives_band] ?? row.lives_band
                : "—"}
            </Td>
            <Td>
              <MoneyCell cents={row.estimated_annual_revenue_cents} />
            </Td>
            <Td className="tabular-nums text-text-secondary">
              {formatPercent(row.probability)}
            </Td>
            <Td className="text-text-secondary">
              {formatRenewal(row.renewal_month, row.renewal_year)}
            </Td>
            <Td>
              {row.cadence_health ? (
                <span
                  className={[
                    "inline-flex rounded-full px-2 py-0.5 text-[11px]",
                    CADENCE_TONE[row.cadence_health]
                      ?? "bg-recessed text-text-secondary",
                  ].join(" ")}
                >
                  {CADENCE_HEALTH_LABEL[row.cadence_health]
                    ?? row.cadence_health}
                </span>
              ) : (
                "—"
              )}
            </Td>
            <Td className="text-text-secondary">
              {formatRelativeDate(row.last_touchpoint_at)}
            </Td>
            <Td className="text-text-secondary">
              {formatRelativeDate(row.created_at)}
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}
